import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';
import { TransitionMotion, spring, presets } from 'react-motion';
import config from '../../../config';
import TickSuccessIcon from '../icons/tick-success/TickSuccessComponent';
import ExclamationIcon from '../icons/exclamation/ExclamationComponent';
import UndoIcon from '../icons/undo/UndoComponent';
import LoadingIcon from '../icons/loading/LoadingComponent';
import ToastCrossIcon from '../icons/toast-cross/ToastCrossComponent';
import ToastButton from '../toast-button/ToastButtonComponent';

import './Toast.scss';

/**
 * Toast that pops out on hover
 */

class ToastComponent extends React.Component {
  constructor() {
    super();
    this.state = {
      toast: [],
    };
    this.timer = {};
    this.count = {};
    this.close = this.close.bind(this);
    this.action = this.action.bind(this);
  }

  componentDidMount() {
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      toast: this.props.data.length ? this.buildToast(this.props.data) : [],
    });
    if (this.props.data.length) {
      this.setupTimer(
        this.props.data[this.props.data.length - 1].id,
        this.props.data[this.props.data.length - 1].timeout,
      );
    }
  }

  UNSAFE_componentWillReceiveProps(props) {
    const propsToast = props.data.length ? props.data[props.data.length - 1] : undefined;
    const stateToast = this.state.toast.length ? this.state.toast[0].data : undefined;
    // if we have a toast in props and one already in state and the id's of these match use this route
    if (props.data.length && this.state.toast.length && propsToast.id === stateToast.id) {
      this.updateExistingToast(propsToast, stateToast);
      // if we have a toast in props and not one in state use this route
    } else if (props.data.length) {
      this.setState({
        toast: this.buildToast(props.data),
      });
      this.setupTimer(propsToast.id, propsToast.timeout);
      // if we have a no toasts in props and not one in state use this route
    } else if (!props.data.length) {
      this.setState({
        toast: [],
      });
    }
  }

  updateExistingToast(propsToast, stateToast) {
    // if the toast is an error toast use this route
    if (propsToast.error) {
      this.setState({
        toast: this.buildToast([{ ...propsToast, failed: this.count[stateToast.id] >= 3, loading: false }]),
      });
      // if we have retried the error toast 3 times or more use this route
      if (this.count[stateToast.id] >= 3) {
        this.removeCount(stateToast.id);
      }
      // if the toast is not an error toast use this route
    } else {
      this.setState({
        toast: this.buildToast([{ ...propsToast, error: false, failed: false, loading: false, success: true }]),
      });
      this.removeCount(stateToast.id);
    }
    this.setupTimer(stateToast.id);
  }

  setupTimer(id, timeout = config.toastLength) {
    this.timer[id] = setTimeout(() => {
      this.props.hideToast(id);
      this.removeTimer(id);
    }, timeout);
  }

  removeTimer(id) {
    clearTimeout(this.timer[id]);
    delete this.timer[id];
  }

  close() {
    const toast = this.state.toast[0];
    if (!toast) {
      return;
    }
    this.removeTimer(toast.data.id);
    this.props.hideToast(toast.data.id);
    this.props.onCloseCallback(toast.data.id);
  }

  action() {
    const toast = this.state.toast[0];
    if (!toast) {
      return;
    }
    this.removeTimer(toast.data.id);
    if (toast.data.error) {
      this.increaseCount(toast.data.id);
    }
    this.setState({
      toast: this.buildToast([{ ...toast.data, loading: true }]),
    });
    toast.data.action();
  }

  increaseCount(id) {
    this.count[id] = this.count[id] ? this.count[id] + 1 : 1;
  }

  removeCount(id) {
    delete this.count[id];
  }

  willLeave() {
    return {
      opacity: spring(0, presets.gentle),
    };
  }

  willEnter() {
    return {
      opacity: 0,
    };
  }

  buildToast(data) {
    return [
      {
        key: `toast--${data[data.length - 1].id}`,
        style: {
          opacity: spring(1, presets.gentle),
        },
        data: {
          id: data[data.length - 1].id,
          action: data[data.length - 1].action,
          message: data[data.length - 1].message,
          error: data[data.length - 1].error,
          failed: data[data.length - 1].failed,
          success: data[data.length - 1].success,
          loading: data[data.length - 1].loading,
        },
      },
    ];
  }

  toastButton(toast) {
    let messageDescriptorKey;
    let icon;

    if (toast.loading) {
      messageDescriptorKey = 'retrying'; // trying...
      icon = <LoadingIcon />;
    } else if (toast.success) {
      messageDescriptorKey = 'success'; // Done!
      icon = <TickSuccessIcon />;
    } else if (toast.failed) {
      messageDescriptorKey = 'failed'; // Failed
      icon = <ExclamationIcon />;
    } else if (toast.error) {
      messageDescriptorKey = 'retry'; // Retry
      icon = <UndoIcon />;
    } else {
      messageDescriptorKey = 'undo'; // Undo
      icon = <UndoIcon />;
    }

    const message = this.context.intl.formatMessage({
      id: `app.toast.message.label.${messageDescriptorKey}`,
      defaultMessage: messageDescriptorKey,
    });
    return (
      <ToastButton
        toastAction={this.action}
        error={toast.error}
        success={toast.success}
        loading={toast.loading}
        failed={toast.failed}
      >
        <div>
          {icon}
          <span>{message}</span>
        </div>
      </ToastButton>
    );
  }

  getToastMessage(toast) {
    const { message } = toast;
    if (message.id === undefined && message.messageDescriptor === undefined) {
      // This is not an "intl" object
      return message;
    }
    // "intl" object, some might be direct and some might have a descriptor + values
    const messageDescriptor = message.messageDescriptor || message;
    return this.context.intl.formatMessage(messageDescriptor, message.values);
  }

  render() {
    return (
      <TransitionMotion willLeave={this.willLeave} willEnter={this.willEnter} styles={this.state.toast}>
        {(interpolatedStyles) => {
          return (
            <div>
              {interpolatedStyles.map((item) => {
                const toast = item.data;
                const errorClass = toast.error ? ' toast-component__error' : '';
                const whichIcon = toast.error ? <ExclamationIcon /> : <TickSuccessIcon />;
                const errorIcon = toast.error ? ' toast-component__icon--error' : '';
                const toastButton = toast.action ? this.toastButton(toast) : '';

                return (
                  <div
                    key={item.key}
                    className={`toast-component ${errorClass}`}
                    style={{ opacity: item.style.opacity }}
                  >
                    <div className="toast-component__container">
                      <div className={`toast-component__icon ${errorIcon}`}>{whichIcon}</div>
                      <span className="toast-component__message">{this.getToastMessage(toast)}</span>
                      {toastButton}
                    </div>
                    <a className="toast-component__close-button" onClick={this.close}>
                      <ToastCrossIcon />
                    </a>
                  </div>
                );
              })}
            </div>
          );
        }}
      </TransitionMotion>
    );
  }
}

ToastComponent.displayName = 'Toast';

ToastComponent.propTypes = {
  /** Array of objects {id: uuid, message: string, action: func, timeout: func}  */
  data: PropTypes.array.isRequired,
  /** Function required to close the toast - insert framework of choice */
  hideToast: PropTypes.func.isRequired,
  /** Function called when the toast is closed by a user interaction
   */
  onCloseCallback: PropTypes.func.isRequired,
};

ToastComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default ToastComponent;
